import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as S from "./PatientsList.style";
import { useTranslation } from "react-i18next";
import ZaiaImage from "../../../../../../zaiaComponents/zaiaImage";
import Popup from "reactjs-popup";
import ProcessName from "../../../../../shared/processName";

// pipes
import * as P from "../../../../../../../utils/pipes";
import ZaiaReaction from "../../../../../../zaiaComponents/zaiaReaction";
import useRecordNavigation from "../../../../../../../hooks/useRecordNavigation";
import { Actions } from "../../../../../../../utils/actionsIds";
import usePatientList from "../../../../../../../hooks/usePatientList";
import { useQuery } from "@apollo/client";
import { QUERY_GET_USER_TO_PROCESS_BY_ISACEPTED, QUERY_GET_USER_TO_PROCESS_COUNT, QUERY_GET_USER_TO_PROCESS_COUNT_FILTERED } from "../../../../../../../graphql/queries/UserToProcess";
import { QUERY_GET_PROCESS_TYPES } from "../../../../../../../graphql/queries/Process";
import Modal from "../../../../../../../utils/modal";
import ModalCreateInvitePatient from "../../../../../shared/modals/modalCreateInvitePatient";
import { useNavigate } from "react-router-dom";

// Routes
import { Routes } from "../../../../../../../utils/constants";
import { ButtonsContainer } from "../followUpView/FollowUpView.style";
import FlorenzSelect from "../../../../../../florenzComponents/florenzSelect";
import FlorenzButton from "../../../../../../florenzComponents/florenzButton";

// Assets
const logoZaiaLoading = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/singularity-health-loader.svg`;
const logoZaiaAlert = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/reaction-error-data.svg`;

function PatientsList({
  filterProcess,
  filterGender,
  inputText,
  setInputText,
  textInput,
  setSelectedPatient,
  selectedPatient
}) {

  const {t, i18n: { language }} = useTranslation("global");
  const { recordAction } = useRecordNavigation();
  const navigate = useNavigate();
  const { PROFILE } = Routes;

  const loadLimit = 20; // Variable para cambiar la cantidad de usuarios cargados fácilmente
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [offset, setOffset] = useState(loadLimit);

  const [usersList, setUsersList] = useState([]);
  const [orderByNames, setOrderByNames] = useState(null);
  const [orderByDate, setOrderByDate] = useState(null);
  const [filterProgram, setFilterProgram] = useState(null);
  const [filterProgramStatus, setFilterProgramStatus] = useState([]);

  const { data: processTypes } = useQuery(QUERY_GET_PROCESS_TYPES);

  const where = {
    isAcepted: { _eq: true },
  };

  if (filterProgram !== null && filterProgram !== undefined) {
    where.processId = { _eq: filterProgram };
  }

  if (filterProgramStatus.length > 0) {
    where.stageId = { _in: filterProgramStatus };
  }

  if (inputText && inputText.trim() !== "") {
    const terms = inputText.trim().split(/\s+/);

    where._and = terms.map(term => ({
      _or: [
        { user: { name: { _ilike: `%${term}%` } } },
        { user: { lastname: { _ilike: `%${term}%` } } },
        { user: { email: { _ilike: `%${term}%` } } },
        { user: { documentNumber: { _ilike: `%${term}%` } } },
      ]
    }));
  }

  const orderBy = useMemo(() => {
  const order = [];

  if (orderByNames) {
    order.push(
      { user: { name: orderByNames } },
      { user: { lastname: orderByNames } }
    );
  }

  if (orderByDate) {
    order.push({
      created_at: orderByDate
    });
  }

  if (order.length === 0) {
    order.push({ created_at: "desc" });
  }

  return order;
}, [orderByNames, orderByDate]);

  const { data: userToProcessesList, loading: userToProcessesListLoading, error: userToProcessesListError, fetchMore } = useQuery(
    QUERY_GET_USER_TO_PROCESS_BY_ISACEPTED, 
    { 
      variables: { 
        where,
        limit: loadLimit, 
        offset: 0, 
        orderBy
      },
      fetchPolicy: "network-only"
    },
  );    

  const { data: userToProcessesCountFiltered } = useQuery(
    QUERY_GET_USER_TO_PROCESS_COUNT_FILTERED, 
    { 
      variables: { 
        where
      },
      fetchPolicy: "network-only"
    },
  );   

  const { data: userToProcessesCount } = useQuery(
    QUERY_GET_USER_TO_PROCESS_COUNT, 
    { 
      fetchPolicy: "network-only"
    },
  );       

  useEffect(() => {
    if (userToProcessesListLoading) return
    if (userToProcessesList?.userToProcess.length <= loadLimit) {
      const users = userToProcessesList.userToProcess;
      setUsersList([]);
      setUsersList(users);
      setHasMore(true);
      setOffset(loadLimit);
    }
  }, [userToProcessesListLoading]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !userToProcessesListLoading && !isFetchingMore) {
      setIsFetchingMore(true);
      fetchMore({
        variables: {
          limit: loadLimit,
          offset: offset,
        },
        updateQuery: (prev, { fetchMoreResult }) => {

          if (!fetchMoreResult || fetchMoreResult.userToProcess.length === 0 || fetchMoreResult.userToProcess.length < loadLimit) {
            setHasMore(false);
            return prev;
          }

          setUsersList([...usersList, ...fetchMoreResult.userToProcess]);
          setOffset(offset + loadLimit);
        }
      }).then(() => {
        setIsFetchingMore(false);
      }).catch(() => {
        setIsFetchingMore(false);
      });
    }
  };

  const handleSelectPatient = (item) => {
    // Evita cambiar `selectedPatient` si es el mismo usuario
    if (item.user.id !== selectedPatient?.id) {
      recordAction(Actions.AINAVIGATOR.OPEN_MODAL_PATIENT);
      setSelectedPatient(item.user);
    }
  };

  const [createInvitePatientActive, setCreateInvitePatientActive] = useState(false);


  const getProcessesByGroup = useMemo(() => {

    const stages = processTypes?.process?.find((item) => item.id === filterProgram)?.processStages;

    if (!stages) return [];

    const allWithoutGroup = stages.every(stage => !stage.groupStage);

    const groupsMap = new Map();

    stages.forEach(stage => {     
      const hasGroup = !!stage.groupStage;
      const groupKey = hasGroup ? stage.groupStage?.value : 0;
      const groupLabel = hasGroup
        ? P.dataTranslation(stage.groupStage?.comment, language)
        : allWithoutGroup ? t("globally.all") : t("globally.others");

      if (!groupsMap.has(groupKey)) {
        groupsMap.set(groupKey, {
          groupName: groupLabel,
          groupKey: groupKey,
          stages: []
        });
      }

      groupsMap.get(groupKey).stages.push(stage);
    });

    return Array.from(groupsMap.values());

  }, [processTypes, filterProgram]);  
  
  const addProgramStatusItem = (id) => {
    if (filterProgramStatus.includes(id)) {
      setFilterProgramStatus(filterProgramStatus.filter(item => item !== id));
    } else {
      setFilterProgramStatus([...filterProgramStatus, id]);
    }
  }

  const addAllProgramStatusFromGroup = (groupKey) => {
    const stages = getProcessesByGroup.find(g => g.groupKey === groupKey)?.stages || [];
    const statusesToAdd = stages.map(stage => stage.id);
    setFilterProgramStatus(prev => [...new Set([...prev, ...statusesToAdd])]);
  }
  const removeAllProgramStatusFromGroup = (groupKey) => {
    const stages = getProcessesByGroup.find(g => g.groupKey === groupKey)?.stages || [];
    const statusesToRemove = stages.map(stage => stage.id);
    setFilterProgramStatus(prev => prev.filter(item => !statusesToRemove.includes(item)));
  }

  const allProgramStatusSelectedByGroup = (groupKey) => {
    const stages = getProcessesByGroup.find(g => g.groupKey === groupKey)?.stages || [];
    const statuses = stages.map(stage => stage.id);
    return statuses.every(status => filterProgramStatus.includes(status));
  }

  if (userToProcessesListError)
    return (
      <S.PatientsList>
        <div className="table-container">
          <div className="zaia-reaction">
            <ZaiaReaction
              zaiaImg={logoZaiaAlert}
              widthimg="100px"
              text={t("globally.errorLoadingPatients")}
              sizeText="15px"
              widthText="240px"
            />
          </div>
        </div>
      </ S.PatientsList>
    )

  if (userToProcessesListLoading)  {
    return (
      <S.PatientsList>
        <div className="table-container">
          <div className="zaia-reaction">
            <ZaiaReaction
              widthimg="150px"
              zaiaImg={logoZaiaLoading}
            />  
          </div>
        </div>
      </S.PatientsList>
    );
  }

  
  

  return (
    <S.PatientsList>
      <Modal open={createInvitePatientActive}>
        <ModalCreateInvitePatient
          setCreateInvitePatientActive={setCreateInvitePatientActive}
        />
      </Modal>
      <div className="table-container" onScroll={handleScroll}>
        <table className="suggestion-table">
          <thead className="suggestion-table__head">
            <tr>
              <th className="filter filter--patient-name"> 
                <div className="filter__space">
                  <button 
                    className={`patient-order`}
                    onClick={() => {
                      setOrderByNames(
                        orderByNames === null ? "asc" : (orderByNames === "desc" ? null : "desc")
                      );
                      setOrderByDate(null);
                      recordAction(Actions.AINAVIGATOR.ORDER_PATIENT_NAME);
                    }}
                  >
                    <i className={`patient-order__icon icon zaia-i-sort-az`}></i>
                    <div className="patient-order__text">
                      {
                        orderByNames === null &&
                        t("aiNavigator.filters.tapToOrder")
                      }
                      {
                        orderByNames !== null &&
                        <>
                          {t("aiNavigator.filters.orderAZ")} <b>
                            {
                              orderByNames === "desc" 
                              ? "Z-A"
                              : orderByNames === "asc" 
                                ? "A-Z"
                                : t("aiNavigator.filters.tapToOrder")
                            }
                          </b>
                        </>
                      }
                    </div>
                  </button>
                </div>
              </th>
              <th className="filter filter--contact">
                <div className="filter__space">
                  <div className="title-header">
                    <i className="title-header__icon icon zaia-i-phone"></i>
                    <div className="title-header__text">
                      {t("aiNavigator.filters.contact")}
                    </div>
                  </div>
                </div>
              </th>
              <th className="filter filter--date">
                <div className="filter__space">
                  <button 
                    className={`patient-order ${orderByDate !== null && "patient-order--actived"}`}
                    onClick={() => {
                      setOrderByDate(
                        orderByDate === null ? "desc" : (orderByDate === "asc" ? null : "asc")
                      )
                      setOrderByNames(null);
                      recordAction(Actions.AINAVIGATOR.ORDER_PATIENT_DATE);
                  }}
                  >
                    <i className={`patient-order__icon icon zaia-i-name-sort`}></i>
                    <div className="patient-order__text">
                      {
                        orderByDate === "asc"
                        ? t("aiNavigator.filters.oldest")
                        : orderByDate === "desc"
                          ? t("aiNavigator.filters.mostRecent")
                          : t("aiNavigator.filters.tapToOrder")
                      }
                    </div>
                  </button>
                </div>
              </th>
              <th className="filter filter--patology">
                <div className="filter__space">
                  <div className="title-header">
                    <div className="title-header__text">
                      {t("aiNavigator.filters.pathologies")}
                    </div>
                  </div>
                </div>
              </th>
              <th className="filter filter--program">
                <div className="filter__space">
                  <Popup
                    trigger={
                      <div>
                        <FlorenzSelect
                          placeholder={t("globally.allPrograms")}
                          label={t("globally.program")}
                          xsSize={true}
                          forWhiteBackgroud={false}
                          items={
                            processTypes?.process?.map((item) => ({
                              value: item.id,
                              text: P.dataTranslation(item.processName, language),
                              img: item.img
                            })) || []
                          }
                          value={filterProgram}
                          noDropdown={true}
                        />                        
                      </div>
                    }
                    closeOnDocumentClick
                    keepTooltipInside
                    position={["bottom center"]}
                    nested
                  >
                    {
                      close => (
                        <S.ProgramSelector>
                          <div className="header">
                            <div className="header__label">{t("globally.program")}</div>
                            <div className="header__label">{t("globally.patients")}</div>
                          </div>
                          <div className="list-programs">
                            <div className={`program-item ${filterProgram === null && "program-item--actived"}`}
                              onClick={() => {
                                setFilterProgram(null);
                                setFilterProgramStatus([]);
                                recordAction(Actions.AINAVIGATOR.FILTER_PATIENT_PROGRAM);
                                close();
                              }}
                            >
                              <div className="program-item__name">{t("globally.all")}</div>
                              <div className="program-item__quantity">
                                <i className="program-item__icon icon zaia-i-patient-mangement"></i>
                                <div className="program-item__number">{userToProcessesCount?.userToProcess_aggregate.aggregate.count || 0}</div>
                              </div>
                            </div>
                            {
                              processTypes?.process.map((item) => {
                                if(item.userToProcesses?.length > 0) {
                                  return (                                 
                                    <div className={`program-item ${filterProgram === item.id && "program-item--actived"}`}
                                      key={item.id}
                                      onClick={() => {
                                        setFilterProgram(item.id);
                                        setFilterProgramStatus([]);
                                        recordAction(Actions.AINAVIGATOR.FILTER_PATIENT_PROGRAM, null, item.id);
                                        close();
                                      }}
                                    >
                                      <div className="program-item__name">{P.dataTranslation(item.processName, language)}</div>
                                      <div className="program-item__quantity">
                                        <i className="program-item__icon icon zaia-i-patient-mangement"></i>
                                        <div className="program-item__number">{item.userToProcesses?.length}</div>
                                      </div>
                                    </div>
                                  )
                                }
                              })
                            }
                          </div>
                        </S.ProgramSelector>
                      )
                    }
                  </Popup>                   
                </div>
              </th>
              <th className="filter filter--program-status">
                <div className="filter__space filter--program-status__space">
                  {
                    <Popup
                      trigger={
                        <div>
                          <FlorenzSelect
                            placeholder={t("followUp.allStages")}
                            label={t("followUp.stage")}
                            xsSize={true}
                            forWhiteBackgroud={false}
                            disabled={!filterProgram}
                            value={filterProgramStatus}
                            selectionText={
                              filterProgramStatus.length > 0 
                              ? filterProgramStatus.length + " " + 
                                (
                                  filterProgramStatus.length === 1
                                    ? t("globally.stageSelected")
                                    : t("globally.stagesSelected")
                                )
                              : t("followUp.allStages")
                            }
                          />                        
                        </div>
                      }
                      closeOnDocumentClick
                      keepTooltipInside
                      position={["bottom center"]}
                      disabled={!filterProgram}
                      nested
                    >
                      {
                        close => (
                          <S.StatusSelector>
                            <div className="header">
                              {t("followUp.programStages")}
                            </div>
                            <div className="list-groups">
                              {
                                getProcessesByGroup.map((group) => (
                                  <div key={group.groupName} className="program-group">
                                    <div className="group-header">
                                      <div className="group-header__text">
                                        {group.groupName}
                                      </div>
                                      <div className="group-header__select"
                                        onClick={() => {
                                          if(allProgramStatusSelectedByGroup(group.groupKey)) {
                                            removeAllProgramStatusFromGroup(group.groupKey);
                                          } else {
                                            addAllProgramStatusFromGroup(group.groupKey);
                                          }
                                        }}
                                      >
                                        {
                                          allProgramStatusSelectedByGroup(group.groupKey)
                                          ? t("aiNavigator.outcomes.clearSelection")
                                          : t("aiNavigator.outcomes.selectAll")	
                                        }
                                      </div>
                                    </div>
                                    <div className="group-stages-list">
                                      {
                                        group.stages.map((stage) => {
                                          return (
                                            <div className="group-stage"
                                              key={stage.id}
                                              onClick={() => {
                                                addProgramStatusItem(stage.id);
                                                recordAction(Actions.AINAVIGATOR.FILTER_PATIENT_PROGRAM_STATUS, null, filterProgram);                                                
                                              }}
                                            >
                                              <i className={`group-stage__check icon ${filterProgramStatus.includes(stage.id) ?  "zaia-i-check-box" : "zaia-i-check-box-outline-blank"}`}/>
                                              <div className="group-stage__title">
                                                {P.dataTranslation(stage.stageName, language)}
                                              </div>
                                            </div>
                                          )
                                        })
                                      }
                                    </div>
                                  </div>
                                ))   
                              }
                            </div>
                          </S.StatusSelector>
                        )
                      } 
                    </Popup>     
                  }
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="suggestion-table__body">
            { 
              usersList?.map((item) => {               
                return (
                  <tr 
                    onClick={
                      () => handleSelectPatient(item)
                    }
                    key={item.id}
                    className="suggestion-table__item"
                    style={{
                      borderTop: inputText ? "1px solid #F6F6FF" : '',
                      borderBottom: inputText ? "" : "1px solid #F6F6FF",
                    }}
                  >
                    <td>
                      <div className="patient-content">
                        <ZaiaImage
                          img={item.user?.picture}
                          alt=""
                          size={25}
                          rounded={true}
                        />
                        <div className="patient-content__info">
                          <div className="patient-content__info-name-psa">
                          {`${item.user.name} ${item.user.lastname}`}
                          </div>
                          {
                            item.user.documentNumber &&
                            <div className="patient-content__info-document">
                              {item.user.enDocumentType?.comment.code} {item.user.documentNumber}
                            </div>
                          }
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="patient-contact">
                        {
                          item.user.countryPhoneId && item.user.phone &&
                          <div className="patient-content__info-phone">
                            {"+" + item.user.phoneCountry?.phonecode + " " + item.user.phone}
                          </div>
                        }
                        <div className="patient-content__info-email">
                          {item.user.email}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="patient-date">
                        {P.shortDate(item.created_at, t, true, language)}
                      </div>
                    </td>
                    <td>
                      <div className="patient-patology">
                        {
                          item.user.userToPatologies.map(
                            (patology) => {
                              return (
                              <div className="patient-content__info-patology">
                                {P.dataTranslation(patology.typePatologyVal.name, language)}
                              </div>
                            )}
                          )
                        }
                      </div>
                    </td>
                    <td>
                      <div className="patient-program">
                        {P.dataTranslation(item.process?.processName, language)}
                      </div>
                    </td>
                    <td>
                      <div className="patient-status">
                        <div className="patient-status__text">
                          {
                            item.processStage?.groupStage &&
                            `${P.dataTranslation(item.processStage?.groupStage.comment, language)} - `
                          }
                          {
                            P.dataTranslation(item.processStage?.stageName, language)
                            + ` ${item.processStage?.incremental ? item.stageExtraInfo : ""}`
                          }
                        </div>
                        <i className="patient-status__icon icon zaia-i-refresh"></i>
                      </div>
                    </td>
                  </tr>
                )
              })
            }
          </tbody>
        </table>  
        {isFetchingMore && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
            <ZaiaReaction widthimg="40px" zaiaImg={logoZaiaLoading} />
          </div>
        )}
      </div>
      <div className="footer">
        <div className="footer__label">
          <div className="footer__label-patients-quantity">
            {t("aiNavigator.your")} <b>{
              userToProcessesCountFiltered?.userToProcess_aggregate.aggregate.count || 0
            }</b> {t("aiNavigator.patientsInOnePlace")}
          </div>
          <div className="footer__label-patients-management">
            {t("aiNavigator.ifYouCantFindThePatient")}, <a onClick={() => {
              navigate(PROFILE.route + "?management=true")
              recordAction(Actions.AINAVIGATOR.GO_PATIENT_MANAGEMENT);
            }}>
              {t("aiNavigator.goToPatientManagement")}
            </a>
          </div>
        </div>
        <div className="footer__button">
          <FlorenzButton
            size="s"
            type="primary"
            text={t("globally.addPatient")}
            onClick={() => {
              recordAction(Actions.AINAVIGATOR.OPEN_MODAL_ADD_PATIENT);
              setCreateInvitePatientActive(true);
            }}
          />
          {/* <button className="button-add-patient"
            onClick={
              () => {
                recordAction(Actions.AINAVIGATOR.OPEN_MODAL_ADD_PATIENT);
                setCreateInvitePatientActive(true);
              }
            }
          >
            <div className="button-add-patient__text">
              {t("globally.addPatient")}
            </div>
            <i className="button-add-patient__icon icon zaia-i-add-user"></i>
          </button> */}
        </div>
      </div>
    </S.PatientsList>
  );
}

export default PatientsList;
